import React, { useMemo, useState } from "react";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { colors } from "../../../Shared/colors";
import { StatusColors } from "./socialLeadsStyles";
import { formatDuration } from "./socialLeadsFormat";

/**
 * Summary strip for the Meta leads screen.
 *
 * Four tiles reconcile what Meta holds against what the CRM has. The
 * assignment spread underneath is CEO-only — it answers "is round-robin
 * distributing leads evenly?", which is an oversight question rather than
 * something an admin acts on while importing.
 *
 * The two waiting-lead facts are clickable: a count of leads nobody has called
 * is useless on its own, since the next question is always "which ones?".
 * Clicking narrows the table below to exactly those rows.
 */
export const SocialLeadsSummary = ({
  stats,
  rows = [],
  showDistribution,
  attention = "",
  onAttentionSelect,
}) => {
  // Prefer server-computed stats; fall back to counting the rows we were given
  // so the tiles still read correctly if the endpoint omits them.
  const counts = useMemo(() => {
    if (stats) return stats;
    return {
      total: rows.length,
      inCrm: rows.filter((r) => r.status === "inCrm").length,
      notImported: rows.filter((r) => r.status === "notImported").length,
      failed: rows.filter((r) => r.status === "failed").length,
    };
  }, [stats, rows]);

  const capturedPct =
    counts.total > 0 ? ((counts.inCrm / counts.total) * 100).toFixed(1) : "0.0";

  // Computed server-side across the whole window. Recomputing it here from the
  // visible rows would make the figure change every time a status tab is
  // clicked, which is exactly what the tiles above already avoid.
  const response = counts.response || null;

  const distribution = useMemo(() => {
    if (!showDistribution) return [];

    // Split by how the lead was assigned, not just who got it. Round-robin only
    // governs first-time enquirers; a returning client goes straight back to
    // whoever already owns them. Lumping the two together makes an even
    // rotation look lopsided and hides whether the rotation itself is at fault.
    const tally = new Map();
    rows.forEach((row) => {
      if (!row.assignedTo?.name) return;
      const key = row.assignedTo.name;
      if (!tally.has(key)) tally.set(key, { roundRobin: 0, returning: 0 });
      const bucket = tally.get(key);
      if (row.isExistingClient) bucket.returning += 1;
      else bucket.roundRobin += 1;
    });

    const entries = Array.from(tally, ([name, counts]) => ({
      name,
      ...counts,
      count: counts.roundRobin + counts.returning,
    })).sort((a, b) => b.count - a.count);

    const max = entries.length ? Math.max(...entries.map((e) => e.count)) : 0;
    return entries.map((entry) => ({
      ...entry,
      pctRoundRobin: max > 0 ? (entry.roundRobin / max) * 100 : 0,
      pctReturning: max > 0 ? (entry.returning / max) * 100 : 0,
    }));
  }, [rows, showDistribution]);

  // Round-robin hands each person the next lead in turn, so over a decent
  // number of leads the counts should sit close together. A wide spread is
  // worth surfacing: it usually means the rotation restarted from the top of
  // the list, which quietly over-feeds whoever sits first.
  const rotation = useMemo(() => {
    const counts = distribution.map((entry) => entry.roundRobin);
    const total = counts.reduce((sum, n) => sum + n, 0);
    if (counts.length < 2 || total < counts.length * 2) return null;

    const low = Math.min(...counts);
    const high = Math.max(...counts);
    return { low, high, uneven: high - low >= 3 && high >= low * 2 };
  }, [distribution]);

  const returningTotal = distribution.reduce((sum, e) => sum + e.returning, 0);

  // When nothing needs importing and nothing failed, the four tiles spend three
  // of their boxes saying "0". Collapsing them to a single line gives the space
  // back to the charts below, and makes the tiles reappearing a signal in
  // itself. Still openable, because the numbers are occasionally worth reading.
  const allHealthy =
    counts.total > 0 && counts.notImported === 0 && counts.failed === 0;
  const [showBreakdown, setShowBreakdown] = useState(false);
  const tilesVisible = !allHealthy || showBreakdown;

  // Only a control when the parent can act on it. Without a handler these stay
  // plain text rather than becoming a button that does nothing when pressed.
  const canDrill = typeof onAttentionSelect === "function";

  // Clicking the fact that is already applied clears it, so the same spot both
  // opens and closes the view — no hunting for a separate reset.
  const drillProps = (value) =>
    canDrill
      ? {
          as: "button",
          type: "button",
          "aria-pressed": attention === value,
          $active: attention === value,
          onClick: () => onAttentionSelect(attention === value ? "" : value),
          title:
            attention === value
              ? "Showing these below — click to show everything again"
              : "Show these leads in the table below",
        }
      : { as: "span" };

  return (
    <>
      {allHealthy ? (
        <HealthyStrip>
          <span className="tick">✓</span>
          <p>
            All <strong>{counts.total}</strong>{" "}
            {counts.total === 1 ? "submission" : "submissions"} in this range were
            captured automatically — nothing waiting, nothing failed.
          </p>
          <button type="button" onClick={() => setShowBreakdown((open) => !open)}>
            {showBreakdown ? "Hide breakdown" : "Show breakdown"}
          </button>
        </HealthyStrip>
      ) : null}

      {tilesVisible ? (
      <Tiles>
        <Tile>
          <p className="label">Submitted on Meta</p>
          <div className="figure">{counts.total}</div>
          <p className="meta">Across the selected date range</p>
        </Tile>
        <Tile $stripe={StatusColors.inCrm}>
          <p className="label">In the CRM</p>
          <div className="figure">{counts.inCrm}</div>
          <p className="meta">{capturedPct}% captured automatically</p>
        </Tile>
        <Tile $stripe={StatusColors.notImported} $figure={StatusColors.notImported}>
          <p className="label">Not imported</p>
          <div className="figure">{counts.notImported}</div>
          <p className="meta">Waiting to be brought in</p>
        </Tile>
        <Tile $stripe={StatusColors.failed} $figure={StatusColors.failed}>
          <p className="label">Failed</p>
          <div className="figure">{counts.failed}</div>
          <p className="meta">Reason shown on each row</p>
        </Tile>
      </Tiles>
      ) : null}

      {response && response.contacted + response.untouched > 0 ? (
        <ResponseStrip $alert={response.overdue > 0}>
          <div className="lead">
            <BoltOutlinedIcon />
            <div>
              <p className="label">Typical time to first contact</p>
              <p className="figure">
                {response.medianMinutesToFirstContact === null
                  ? "No leads contacted yet"
                  : formatDuration(response.medianMinutesToFirstContact)}
              </p>
            </div>
          </div>

          <div className="facts">
            <span className="fact">
              <strong>{response.withinSla}</strong> of{" "}
              <strong>{response.contacted}</strong> answered within{" "}
              {formatDuration(response.slaMinutes)}
            </span>
            {response.overdue > 0 ? (
              <Drill
                {...drillProps("overdue")}
                className="fact alert"
                $tone="alert"
              >
                <span>
                  <strong>{response.overdue}</strong>{" "}
                  {response.overdue === 1 ? "lead has" : "leads have"} been
                  waiting over {formatDuration(response.slaMinutes)} — nobody has
                  called yet
                </span>
                {canDrill ? <ArrowForwardIcon className="go" /> : null}
              </Drill>
            ) : response.untouched > 0 ? (
              <Drill
                {...drillProps("waiting")}
                className="fact muted"
                $tone="muted"
              >
                <span>
                  <strong>{response.untouched}</strong> not contacted yet, all
                  still inside {formatDuration(response.slaMinutes)}
                </span>
                {canDrill ? <ArrowForwardIcon className="go" /> : null}
              </Drill>
            ) : (
              <span className="fact good">Every lead in this range has been contacted</span>
            )}
          </div>
        </ResponseStrip>
      ) : null}

      {showDistribution && distribution.length > 0 && (
        <Distribution>
          <div className="head">
            <h2>Assignment spread — selected range</h2>
            <div className="legend">
              <span className="key">
                <i className="swatch rr" /> Round-robin
              </span>
              {returningTotal > 0 ? (
                <span className="key">
                  <i className="swatch ret" /> Returning client
                </span>
              ) : null}
            </div>
          </div>

          <div className="bars">
            {distribution.map((item) => (
              <div className="bar" key={item.name}>
                <span className="who">{item.name}</span>
                <span className="track">
                  {item.roundRobin > 0 ? (
                    <Tooltip
                      title={`${item.roundRobin} new ${
                        item.roundRobin === 1 ? "enquirer" : "enquirers"
                      } via round-robin`}
                    >
                      <span
                        className="fill rr"
                        style={{ width: `${item.pctRoundRobin}%` }}
                      />
                    </Tooltip>
                  ) : null}
                  {item.returning > 0 ? (
                    <Tooltip
                      title={`${item.returning} returning ${
                        item.returning === 1 ? "client" : "clients"
                      } — went back to their existing owner, not through the rotation`}
                    >
                      <span
                        className="fill ret"
                        style={{ width: `${item.pctReturning}%` }}
                      />
                    </Tooltip>
                  ) : null}
                </span>
                <span className="n">{item.count}</span>
              </div>
            ))}
          </div>

          {rotation ? (
            <p className={`note${rotation.uneven ? " warn" : ""}`}>
              {rotation.uneven ? (
                <>
                  Round-robin gave between <strong>{rotation.low}</strong> and{" "}
                  <strong>{rotation.high}</strong> leads per person. It should be
                  close to even — a gap this wide usually means the rotation
                  restarted from the top of the list.
                </>
              ) : (
                <>
                  Round-robin is even: <strong>{rotation.low}</strong>–
                  <strong>{rotation.high}</strong> leads per person.
                  {returningTotal > 0
                    ? " Any wider gaps above are returning clients going back to their existing owner."
                    : ""}
                </>
              )}
            </p>
          ) : null}
        </Distribution>
      )}
    </>
  );
};

const Tiles = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(178px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Tile = styled.div`
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 3px;
    background: ${(props) => props.$stripe || colors.brown};
  }

  .label {
    margin: 0 0 6px 0;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: ${colors.textLight};
  }

  .figure {
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.5px;
    font-variant-numeric: tabular-nums;
    color: ${(props) => props.$figure || colors.text};
  }

  .meta {
    margin: 6px 0 0 0;
    font-size: 0.72rem;
    color: ${colors.textMed};
  }
`;

const HealthyStrip = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem 0.85rem;
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 3px solid ${StatusColors.inCrm};
  border-radius: 12px;
  padding: 12px 16px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);

  .tick {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    font-size: 0.72rem;
    font-weight: 700;
    color: ${colors.white};
    background: ${StatusColors.inCrm};
    flex-shrink: 0;
  }

  p {
    margin: 0;
    flex: 1;
    min-width: 220px;
    font-size: 0.83rem;
    color: ${colors.textMed};
  }
  p strong {
    color: ${colors.text};
    font-variant-numeric: tabular-nums;
  }

  button {
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    padding: 5px 11px;
    border-radius: 7px;
    cursor: pointer;
    color: ${colors.brown};
    background: ${colors.white};
    border: 1px solid ${colors.inputBorderWhite};
    white-space: nowrap;

    &:hover {
      border-color: ${colors.brown};
    }
    &:focus-visible {
      outline: 2px solid ${colors.textBlue};
      outline-offset: 2px;
    }
  }
`;

const ResponseStrip = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 0.9rem 1.5rem;
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-left: 3px solid
    ${(props) => (props.$alert ? colors.veryHot : colors.textBlue)};
  border-radius: 12px;
  padding: 13px 16px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);

  .lead {
    display: flex;
    align-items: center;
    gap: 11px;
    min-width: 0;
  }
  .lead svg {
    font-size: 1.45rem;
    color: ${(props) => (props.$alert ? colors.veryHot : colors.textBlue)};
    flex-shrink: 0;
  }
  .label {
    margin: 0 0 3px 0;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: ${colors.textLight};
  }
  .figure {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -0.3px;
    font-variant-numeric: tabular-nums;
    color: ${colors.text};
  }

  .facts {
    display: flex;
    flex-direction: column;
    gap: 4px;
    align-items: flex-end;
    text-align: right;

    @media (max-width: 720px) {
      align-items: flex-start;
      text-align: left;
    }
  }
  .fact {
    font-size: 0.78rem;
    color: ${colors.textMed};
  }
  .fact strong {
    font-variant-numeric: tabular-nums;
    color: ${colors.text};
  }
  .fact.alert,
  .fact.alert strong {
    color: ${colors.veryHot};
    font-weight: 600;
  }
  .fact.good {
    color: ${StatusColors.inCrm};
    font-weight: 600;
  }
  .fact.muted {
    color: ${colors.textLight};
  }
`;

/**
 * A summary fact you can click to filter the table below.
 *
 * Styled as text rather than a button on purpose — it sits inside a sentence,
 * and a solid button here would compete with "Import into CRM" for the eye.
 * The arrow and the hover tint carry the affordance instead. Colour comes from
 * the `.fact` classes on ResponseStrip so the alert stays red either way.
 */
const Drill = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font: inherit;
  font-size: 0.78rem;
  text-align: inherit;
  border: 1px solid transparent;
  border-radius: 8px;
  padding: 4px 8px;
  margin: -4px -8px;
  background: ${(props) =>
    props.$active
      ? props.$tone === "alert"
        ? "rgba(232, 11, 11, 0.09)"
        : colors.inputWhite
      : "transparent"};
  transition: background 0.18s ease, border-color 0.18s ease;

  /* Only the button form is interactive; as a span it must stay inert. */
  &:is(button) {
    cursor: pointer;
  }

  .go {
    font-size: 0.95rem;
    flex-shrink: 0;
    opacity: ${(props) => (props.$active ? 1 : 0.55)};
    transition: transform 0.18s ease, opacity 0.18s ease;
  }

  &:is(button):hover {
    background: ${(props) =>
      props.$tone === "alert" ? "rgba(232, 11, 11, 0.09)" : colors.inputWhite};
    border-color: ${(props) =>
      props.$tone === "alert" ? "rgba(232, 11, 11, 0.25)" : colors.inputBorderWhite};
  }
  &:is(button):hover .go {
    opacity: 1;
    transform: translateX(2px);
  }
  &:is(button):focus-visible {
    outline: 2px solid ${colors.textBlue};
    outline-offset: 1px;
  }

  &[aria-pressed="true"] {
    border-color: ${(props) =>
      props.$tone === "alert" ? "rgba(232, 11, 11, 0.3)" : colors.inputBorderWhite};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    .go {
      transition: none;
    }
    &:is(button):hover .go {
      transform: none;
    }
  }
`;

const Distribution = styled.div`
  background: ${colors.white};
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 12px;
  padding: 14px 16px;
  margin-bottom: 1rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08), 0 1px 3px rgba(0, 0, 0, 0.1);

  .head {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem 1rem;
    margin-bottom: 12px;
  }

  h2 {
    margin: 0;
    font-size: 0.68rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.7px;
    color: ${colors.textLight};
  }

  .legend {
    display: flex;
    flex-wrap: wrap;
    gap: 0.85rem;
  }
  .key {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.72rem;
    color: ${colors.textMed};
  }
  .swatch {
    width: 9px;
    height: 9px;
    border-radius: 3px;
    display: inline-block;
  }
  .swatch.rr {
    background: ${colors.brown};
  }
  .swatch.ret {
    background: ${colors.purple};
  }

  .bars {
    display: flex;
    flex-direction: column;
    gap: 9px;
  }

  .note {
    margin: 12px 0 0 0;
    padding-top: 11px;
    border-top: 1px solid ${colors.inputBorderWhite};
    font-size: 0.76rem;
    color: ${colors.textMed};
  }
  .note strong {
    color: ${colors.text};
    font-variant-numeric: tabular-nums;
  }
  .note.warn {
    color: ${colors.moderate};
  }
  .note.warn strong {
    color: ${colors.moderate};
  }

  .bar {
    display: grid;
    grid-template-columns: 160px 1fr 42px;
    gap: 12px;
    align-items: center;

    @media (max-width: 600px) {
      grid-template-columns: 110px 1fr 34px;
    }
  }

  .who {
    font-size: 0.8rem;
    color: ${colors.text};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .track {
    display: flex;
    height: 7px;
    background: ${colors.inputWhite};
    border-radius: 20px;
    overflow: hidden;
  }

  /* Two segments, each sized by what it represents. The previous single bar
     used a brown-to-blue gradient that encoded nothing — colour now carries
     the one distinction that explains an uneven spread. */
  .fill {
    display: block;
    height: 100%;
    min-width: 2px;
  }
  .fill.rr {
    background: ${colors.brown};
  }
  .fill.ret {
    background: ${colors.purple};
  }
  .fill:first-child {
    border-top-left-radius: 20px;
    border-bottom-left-radius: 20px;
  }
  .fill:last-child {
    border-top-right-radius: 20px;
    border-bottom-right-radius: 20px;
  }

  .n {
    font-size: 0.8rem;
    font-weight: 700;
    text-align: right;
    font-variant-numeric: tabular-nums;
    color: ${colors.text};
  }
`;
